import { z } from 'zod'

export const RECORDING_MAX_BYTES = 20 * 1024 * 1024
export const RECORDING_REQUEST_MAX_BYTES = 30 * 1024 * 1024
export const RecordingId = z.string().uuid()
export const RecordingMetadata = z.object({
  id: RecordingId,
  content_sha256: z
    .string()
    .regex(/^[0-9a-f]{64}$/)
    .nullable(),
  deleted: z.boolean(),
})
export const RecordingListQuery = z.object({
  cursor: RecordingId.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(100),
})
export const RecordingListResponse = z.object({
  items: z.array(RecordingMetadata),
  next_cursor: RecordingId.nullable(),
})
export const RecordingPutRequest = z
  .object({
    track_data: z
      .string()
      .min(1)
      .max(4 * Math.ceil(RECORDING_MAX_BYTES / 3)),
  })
  .strict()
export const RecordingDetail = z.object({
  id: RecordingId,
  content_sha256: z.string().regex(/^[0-9a-f]{64}$/),
  track_data: z.string(),
})

export type RecordingMetadata = z.infer<typeof RecordingMetadata>

// Swift JSONEncoder's default Date representation is seconds since 2001-01-01.
const date = z.number().finite().min(-63113904000).max(252423993599)
const scalar = z.number().finite().min(0).max(1e9)
const point = z
  .object({
    latitude: z.number().finite().min(-90).max(90),
    longitude: z.number().finite().min(-180).max(180),
    elevation: z.number().finite().min(-100000).max(100000).nullish(),
  })
  .passthrough()
export const PrivateRecordingTrack = z
  .object({
    id: RecordingId,
    startedAt: date,
    endedAt: date.nullish(),
    activity: z.enum(['hiking', 'running', 'cycling']),
    distanceM: scalar,
    ascentM: scalar,
    descentM: scalar.optional(),
    movingDurationS: scalar,
    referenceRouteId: z.string().nullish(),
    referenceRouteName: z.string().nullish(),
    samples: z.array(
      z
        .object({
          timestamp: date,
          coord: point,
          horizontalAccuracyM: z.number().finite().min(-1).max(1e6),
          verticalAccuracyM: z.number().finite().min(-1).max(1e6).nullish(),
          speedMPS: z.number().finite().min(-1).max(1000).nullish(),
          speedAccuracyMPS: z.number().finite().min(-1).max(1000).nullish(),
          courseDeg: z.number().finite().min(-1).max(360).nullish(),
        })
        .passthrough(),
    ),
    waypoints: z.array(
      z
        .object({
          id: RecordingId,
          kind: z.enum([
            'start',
            'finish',
            'viewpoint',
            'water',
            'supply',
            'camp',
            'danger',
            'junction',
            'toilet',
            'generic',
            'note',
            'startFinish',
            'navTarget',
          ]),
          point,
          note: z.string().nullish(),
          display_name: z.string().nullish(),
          recorded_at: date.nullish(),
          attachment_ids: z.array(RecordingId).optional(),
          source_metadata: z
            .object({
              format: z.enum(['gpx', 'kml']),
              originalType: z.string().nullish(),
              originalSymbol: z.string().nullish(),
              originalDescription: z.string().nullish(),
              originalComment: z.string().nullish(),
              styleURL: z.string().nullish(),
            })
            .passthrough()
            .nullish(),
        })
        .passthrough(),
    ),
  })
  .passthrough()
  .refine((track) => track.endedAt == null || track.endedAt >= track.startedAt, {
    message: 'endedAt must not precede startedAt',
  })
